import { GetProcess, IsObject } from '../helpers/helpers';
import { ConstructClassWithPrivateMembers } from '../helpers/factory/ClassWithPrivateMembers';
import { BaseClass, HasFactoryWaterMark, MakeFactory } from '../helpers/factory/factory';
import { GENERIC_CONFIG, POSIX_CONFIG, WINDOWS_CONFIG } from '../core/configs';
import { CommonBasePathSegments, JoinPathSegments, RelativePathSegments, ResolvePathSegmentsWithOptionalRoot } from '../core/operations';
import { PathSegmentsToStringPath, StringPathToPathSegments } from '../core/convert';
import { AreSamePathSegments, BaseNamePathSegments, DirNamePathSegments, IsAbsolutePathSegments, IsRootPathSegments, IsSubPathSegments, StemAndExtTuplePathSegments } from '../core/infos';
import { ForcePathSegmentsAsAbsolute, ForcePathSegmentsAsRelative, PathSegmentsPop, PathSegmentsPush } from '../core/mutate';
import { GetProcessPathSegments } from '../core/others';
import { NormalizePathSegments } from '../core/normalize';
export const PATH_PRIVATE = Symbol('path-private');
let CHECK_PATH_CONSTRUCT = true;
export function NewPath(path, config, constructor = Path) {
    CHECK_PATH_CONSTRUCT = false;
    const _path = new constructor(path, config);
    CHECK_PATH_CONSTRUCT = true;
    return _path;
}
export function NewPathFromInstance(instance, path = instance.toArray(), config = instance.config) {
    return NewPath(path, config, instance.constructor);
}
export function ConstructPath(instance, path, config) {
    ConstructClassWithPrivateMembers(instance, PATH_PRIVATE);
    const privates = instance[PATH_PRIVATE];
    if (CHECK_PATH_CONSTRUCT) {
        if (config === void 0) {
            privates.config = IsPath(path)
                ? path[PATH_PRIVATE].config
                : GENERIC_CONFIG;
        }
        else if (IsObject(config)) {
            privates.config = Object.isFrozen(config) ? config : Object.freeze({ ...config });
        }
        else {
            throw new TypeError(`Expected PlatformConfig or void as config`);
        }
        if (typeof path === 'string') {
            privates.path = StringPathToPathSegments(path, privates.config);
        }
        else if (Array.isArray(path)) {
            privates.path = NormalizePathSegments(path, privates.config);
        }
        else if (IsPath(path)) {
            privates.path = path[PATH_PRIVATE].path.slice();
        }
        else if (typeof path.toString === 'function') {
            privates.path = StringPathToPathSegments(path.toString(), privates.config);
        }
        else {
            throw new TypeError(`Expected string, string[], Path or object with toString() method as path`);
        }
    }
    else {
        privates.path = path;
        privates.config = config;
    }
}
export function IsPath(value) {
    return IsObject(value)
        && value.hasOwnProperty(PATH_PRIVATE);
}
const IS_PATH_CONSTRUCTOR = Symbol('is-path-constructor');
export function IsPathConstructor(value, direct) {
    return (typeof value === 'function') && ((value === Path) || HasFactoryWaterMark(value, IS_PATH_CONSTRUCTOR, direct));
}
export function PathGetConfig(instance) {
    return instance[PATH_PRIVATE].config;
}
export function PathGetLength(instance) {
    return instance[PATH_PRIVATE].path.length;
}
export function PathIsAbsolute(instance) {
    const privates = instance[PATH_PRIVATE];
    return IsAbsolutePathSegments(privates.path, privates.config);
}
export function PathIsRoot(instance) {
    const privates = instance[PATH_PRIVATE];
    return IsRootPathSegments(privates.path, privates.config);
}
export function PathEquals(instance, path) {
    return AreSamePathSegments(instance[PATH_PRIVATE].path, PathOfFromInstance(instance, path)[PATH_PRIVATE].path);
}
export function PathIsSubPathOf(instance, parentPath) {
    return IsSubPathSegments(instance[PATH_PRIVATE].path, PathOfFromInstance(instance, parentPath)[PATH_PRIVATE].path);
}
export function PathDirname(instance) {
    const dirname = DirNamePathSegments(instance[PATH_PRIVATE].path);
    return (dirname === null)
        ? null
        : NewPathFromInstance(instance, dirname);
}
export function PathBasename(instance, ext, allowedSpecialSegments) {
    const privates = instance[PATH_PRIVATE];
    return BaseNamePathSegments(privates.path, ext, (allowedSpecialSegments === void 0)
        ? privates.config
        : {
            ...privates.config,
            allowedSpecialSegments: new Set(Array.from(allowedSpecialSegments))
        });
}
export function PathStemAndExt(instance) {
    const privates = instance[PATH_PRIVATE];
    return StemAndExtTuplePathSegments(privates.path, privates.config);
}
export function PathCommonBase(instance, paths) {
    const commonBase = CommonBasePathSegments([
        instance[PATH_PRIVATE].path,
        ...paths.map(path => PathOfFromInstance(instance, path)[PATH_PRIVATE].path)
    ]);
    return (commonBase === null)
        ? null
        : NewPathFromInstance(instance, commonBase);
}
export function PathConcat(instance, paths) {
    const privates = instance[PATH_PRIVATE];
    return NewPathFromInstance(instance, JoinPathSegments([
        privates.path,
        ...paths.map(path => PathOfFromInstance(instance, path)[PATH_PRIVATE].path)
    ], privates.config));
}
export function PathRelative(instance, path) {
    const privates = instance[PATH_PRIVATE];
    const relativePath = RelativePathSegments(privates.path, PathOfFromInstance(instance, path)[PATH_PRIVATE].path, privates.config);
    return (relativePath === null)
        ? null
        : NewPathFromInstance(instance, relativePath);
}
export function PathResolve(instance, root) {
    const privates = instance[PATH_PRIVATE];
    return NewPathFromInstance(instance, ResolvePathSegmentsWithOptionalRoot(privates.path, (root === void 0)
        ? void 0
        : PathOfFromInstance(instance, root)[PATH_PRIVATE].path, privates.config));
}
export function PathClone(instance, config) {
    const privates = instance[PATH_PRIVATE];
    return NewPathFromInstance(instance, privates.path.slice(), config);
}
export function PathPush(instance, parts) {
    const privates = instance[PATH_PRIVATE];
    for (let i = 0, l = parts.length; i < l; i++) {
        PathSegmentsPush(privates.path, parts[i], privates.config);
    }
}
export function PathPop(instance) {
    PathSegmentsPop(instance[PATH_PRIVATE].path);
}
export function PathForceAbsolute(instance, root) {
    const privates = instance[PATH_PRIVATE];
    const _root = (root === void 0)
        ? GetProcessPathSegments(privates.config)
        : PathOfFromInstance(instance, root)[PATH_PRIVATE].path;
    ForcePathSegmentsAsAbsolute(privates.path, _root[0], privates.config);
}
export function PathForceRelative(instance) {
    const privates = instance[PATH_PRIVATE];
    ForcePathSegmentsAsRelative(privates.path, privates.config);
}
export function PathItem(instance, index) {
    const path = instance[PATH_PRIVATE].path;
    if ((0 <= index) && (index < path.length)) {
        return path[index];
    }
    else {
        throw new RangeError(`Index out of range [0, ${path.length}[`);
    }
}
export function PathEvery(instance, callback) {
    return instance[PATH_PRIVATE].path.every((value, index) => {
        return callback.call(instance, value, index);
    });
}
export function PathSome(instance, callback) {
    return instance[PATH_PRIVATE].path.some((value, index) => {
        return callback.call(instance, value, index);
    });
}
export function PathForEach(instance, callback) {
    return instance[PATH_PRIVATE].path.forEach((value, index) => {
        callback.call(instance, value, index);
    });
}
export function PathToIterator(instance) {
    return instance[PATH_PRIVATE].path[Symbol.iterator]();
}
export function PathToString(instance, separator) {
    const privates = instance[PATH_PRIVATE];
    let config;
    if (separator === void 0) {
        config = privates.config;
    }
    else if (typeof separator === 'string') {
        config = (separator === privates.config.separator)
            ? privates.config
            : { ...privates.config, separator };
    }
    else {
        throw new TypeError(`Expected string as separator`);
    }
    return PathSegmentsToStringPath(privates.path, config);
}
export function PathToArray(instance) {
    return instance[PATH_PRIVATE].path.slice();
}
export function PathToURL(instance) {
    const url = new URL('file://');
    url.pathname = PathToString(instance, '/');
    return url;
}
export function PathCurrentPlatform() {
    return (GetProcess().platform === 'win32')
        ? WINDOWS_CONFIG
        : POSIX_CONFIG;
}
export function PathProcess(constructor, config) {
    return new constructor(GetProcess().cwd(), config);
}
export function PathOf(constructor, path, config) {
    return (path instanceof constructor)
        ? path
        : new constructor(path, config);
}
export function PathOfFromInstance(instance, path, config = instance.config) {
    return PathOf(instance.constructor, path, config);
}
function PurePathFactory(superClass) {
    return class Path extends superClass {
        static get posix() {
            return POSIX_CONFIG;
        }
        static get windows() {
            return WINDOWS_CONFIG;
        }
        static get generic() {
            return GENERIC_CONFIG;
        }
        static get currentPlatform() {
            return PathCurrentPlatform();
        }
        static process(config) {
            return PathProcess(this, config);
        }
        static of(path, config) {
            return PathOf(this, path, config);
        }
        constructor(...args) {
            const [path, config] = args[0];
            super(...args.slice(1));
            ConstructPath(this, path, config);
        }
        get config() {
            return PathGetConfig(this);
        }
        get length() {
            return PathGetLength(this);
        }
        isAbsolute() {
            return PathIsAbsolute(this);
        }
        isRoot() {
            return PathIsRoot(this);
        }
        equals(path) {
            return PathEquals(this, path);
        }
        isSubPathOf(parentPath) {
            return PathIsSubPathOf(this, parentPath);
        }
        dirname() {
            return PathDirname(this);
        }
        basename(ext, allowedSpecialSegments) {
            return PathBasename(this, ext, allowedSpecialSegments);
        }
        stemAndExt() {
            return PathStemAndExt(this);
        }
        commonBase(...paths) {
            return PathCommonBase(this, paths);
        }
        concat(...paths) {
            return PathConcat(this, paths);
        }
        relative(path) {
            return PathRelative(this, path);
        }
        resolve(root) {
            return PathResolve(this, root);
        }
        clone(config) {
            return PathClone(this, config);
        }
        push(...parts) {
            PathPush(this, parts);
            return this;
        }
        pop() {
            PathPop(this);
            return this;
        }
        forceAbsolute(root) {
            PathForceAbsolute(this, root);
            return this;
        }
        forceRelative() {
            PathForceRelative(this);
            return this;
        }
        item(index) {
            return PathItem(this, index);
        }
        every(callback) {
            return PathEvery(this, callback);
        }
        some(callback) {
            return PathSome(this, callback);
        }
        forEach(callback) {
            return PathForEach(this, callback);
        }
        [Symbol.iterator]() {
            return PathToIterator(this);
        }
        toString(separator) {
            return PathToString(this);
        }
        toArray() {
            return PathToArray(this);
        }
        toJSON() {
            return PathToString(this);
        }
        toURL() {
            return PathToURL(this);
        }
    };
}
export let Path;
export function PathFactory(superClass) {
    return MakeFactory(PurePathFactory, [], superClass, {
        name: 'Path',
        instanceOf: Path,
        waterMarks: [IS_PATH_CONSTRUCTOR]
    });
}
Path = class Path extends PathFactory(BaseClass) {
    constructor(path, config) {
        super([path, config]);
    }
};
//# sourceMappingURL=implementation.js.map